/**
 * Page-level access guards for Asset ROPA workflows.
 *
 * These checks are intentionally isolated from menu/routing permission logic
 * (`compareWithRoutingPermissions`, `OrganizationSection` filters).
 */

import canAccessProcessingActivitiesAdmin from 'config/permissions/canAccessProcessingActivitiesAdmin';
import RolesOrganization from 'config/permissions/RolesOrganization';

type UserDataSlice = {
  is_asset_owner?: boolean;
  roles?: RolesOrganization[];
};

/** Unchanged guard for the administrator Asset ROPA page (Use Case E). */
export const canAccessAdminAssetRopaPage = (userData: UserDataSlice): boolean => canAccessProcessingActivitiesAdmin(userData);

/**
 * Owner Asset ROPA page (Use Case F).
 * BE is expected to set `is_asset_owner` on the user session when the owner role is confirmed.
 * Until then, owners will not see the menu item (`requiresAssetOwner`) or pass this guard.
 *
 * Dev-only preview: set REACT_APP_MOCK_OWNER_ASSET_ROPA_ACCESS=true locally (never in production).
 */
export const canAccessOwnerAssetRopaPage = (userData: UserDataSlice): boolean => {
  if (userData.is_asset_owner === true) {
    return true;
  }

  return process.env.NODE_ENV !== 'production' && process.env.REACT_APP_MOCK_OWNER_ASSET_ROPA_ACCESS === 'true';
};

/** Administrator review detail page for a submitted APA (Use Case I). */
export const canAccessAdminApaReviewPage = (userData: UserDataSlice): boolean => canAccessProcessingActivitiesAdmin(userData);

/** Asset owner APA editor page (Use Case F). ROPA admins may open in view mode from PA detail. */
export const canAccessOwnerApaEditorPage = (userData: UserDataSlice): boolean =>
  canAccessOwnerAssetRopaPage(userData) || canAccessProcessingActivitiesAdmin(userData);

/** DPIA need assessment page (Use Case J). Admins may edit; asset owners may view. */
export const canAccessDpiaNeedAssessmentPage = (userData: UserDataSlice): boolean =>
  canAccessProcessingActivitiesAdmin(userData) || canAccessOwnerAssetRopaPage(userData);

export const canEditDpiaNeedAssessment = (userData: UserDataSlice): boolean => canAccessProcessingActivitiesAdmin(userData);

/** Owner DPIA editor page (Use Case K). */
export const canAccessOwnerDpiaEditorPage = (userData: UserDataSlice): boolean => canAccessOwnerAssetRopaPage(userData);

/** Administrator DPIA review page (Use Case L). */
export const canAccessAdminDpiaReviewPage = (userData: UserDataSlice): boolean => canAccessProcessingActivitiesAdmin(userData);

/** Purpose LIA page (Use Case M). */
export const canAccessPurposeLiaPage = (userData: UserDataSlice): boolean => canAccessProcessingActivitiesAdmin(userData);
